import {
  Avatar,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuList,
  Portal,
} from '@chakra-ui/react';
import { IconArrowBoxLeft } from 'central-icons/IconArrowBoxLeft';
import { IconEyeOpen } from 'central-icons/IconEyeOpen';
import { IconPeople } from 'central-icons/IconPeople';
import { IconSettingsGear2 } from 'central-icons/IconSettingsGear2';
import { IconShieldKeyhole } from 'central-icons/IconShieldKeyhole';
import { IconTrashCan } from 'central-icons/IconTrashCan';
import type { ComponentType } from 'react';
import { useNavigate } from 'react-router';

import {
  clearStorage,
  DASH_META_ROOT,
  fullLogout,
  isRoleRevelioAdmin,
  useGetLoggedInUser,
} from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';
import { Role } from '@revelio/data-access';
import {
  Menu as UiMenu,
  MenuButton as UiMenuButton,
  MenuDivider as UiMenuDivider,
  MenuGroup as UiMenuGroup,
  MenuItem as UiMenuItem,
  MenuList as UiMenuList,
} from '@revelio/ui';

import { useToast } from '../../../../shared/hooks/useToast';
import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { IconUserAvatar } from '../custom-icons';
import navStyles from '../side-nav.module.css';
import { MenuItemWithIcon } from './menu-item-with-icon';
import menuStyles from './menus.module.css';

interface AccountMenuProps {
  version?: string;
  onOpenImpersonationModal?: () => void;
}

type AccountMenuEntry =
  | { key: string; divider: true }
  | {
      key: string;
      label: string;
      icon: ComponentType<{ size?: number | string }>;
      onClick?: () => void;
      testId?: string;
    };

export function AccountMenu({
  version,
  onOpenImpersonationModal,
}: AccountMenuProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);

  const { loggedInUser, realLoggedInUser } = useGetLoggedInUser();
  const isImpersonationEnabled = useUnleashFlag(FeatureFlag.Impersonation);
  const adminRole = realLoggedInUser?.role ?? loggedInUser.role;
  const isRevelioAdmin = isRoleRevelioAdmin(adminRole);
  const userId = loggedInUser.id;
  const username = loggedInUser.name;

  const handleClearCache = async () => {
    const clearSiteData = fetch(
      `${DASH_META_ROOT}/api/auth/clear-site-data?types="cache", "storage"`,
      {
        credentials: 'include',
        headers: { 'request-id': crypto.randomUUID() },
      }
    );

    const manualClear = clearStorage();

    const clearAll = Promise.all([clearSiteData, manualClear]);

    toast.promise(clearAll, {
      loading: {
        title: 'Clearing Cache',
        description: 'Please wait a moment...',
        position: 'top-right',
      },
      success: {
        title: 'Cache Cleared',
        position: 'top-right',
        duration: 1000,
        onCloseComplete: () => window.location.reload(),
      },
      error: (error) => ({
        title: 'Error Clearing Cache',
        description: 'Please try again.',
        position: 'top-right',
        onCloseComplete: () => console.error(error),
      }),
    });
  };

  const entries: AccountMenuEntry[] = [
    {
      key: 'account',
      label: 'Account',
      icon: IconSettingsGear2,
      onClick: () => navigate(`/manage/user/${userId}/edit`),
    },
    ...(isRevelioAdmin
      ? [
          {
            key: 'admin',
            label: 'Admin',
            icon: IconShieldKeyhole,
            onClick: () => navigate('/admin'),
          },
        ]
      : []),
    ...(isRevelioAdmin && isImpersonationEnabled
      ? [
          {
            key: 'impersonation',
            label: 'Impersonation Settings',
            icon: IconEyeOpen,
            onClick: onOpenImpersonationModal,
            testId: 'impersonation-settings-menu-item',
          },
        ]
      : []),
    { key: 'divider-main', divider: true },
    ...(isRevelioAdmin
      ? [
          {
            key: 'clear-cache',
            label: 'Clear Cache',
            icon: IconTrashCan,
            onClick: handleClearCache,
          },
        ]
      : []),
    ...(loggedInUser.role === Role.ClientAdmin
      ? [
          {
            key: 'user-management',
            label: 'User Management',
            icon: IconPeople,
            onClick: () => navigate('/manage'),
          },
        ]
      : []),
    {
      key: 'logout',
      label: 'Logout',
      icon: IconArrowBoxLeft,
      onClick: () => fullLogout(true),
    },
  ];

  if (isNewDashboardLayout) {
    return (
      <UiMenu>
        <UiMenuButton>
          <button
            type="button"
            aria-label="Account menu"
            data-testid="user-controls"
            className={menuStyles.triggerButton}
          >
            <Avatar
              size="sm"
              className={`${menuStyles.avatar} ${navStyles.legacyRailIcon}`}
            />
            <IconUserAvatar className={navStyles.themedRailIcon} />
          </button>
        </UiMenuButton>
        <UiMenuList side="right" align="start">
          <UiMenuGroup label={username as string}>
            {entries.map((entry) => {
              if ('divider' in entry) {
                return <UiMenuDivider key={entry.key} />;
              }
              const Icon = entry.icon;
              return (
                <UiMenuItem
                  key={entry.key}
                  icon={<Icon size={16} />}
                  onClick={entry.onClick}
                  data-testid={entry.testId}
                >
                  {entry.label}
                </UiMenuItem>
              );
            })}
          </UiMenuGroup>
          {version && (
            <>
              <UiMenuDivider />
              <UiMenuGroup label={version} className={menuStyles.versionGroup}>
                {null}
              </UiMenuGroup>
            </>
          )}
        </UiMenuList>
      </UiMenu>
    );
  }

  return (
    <Menu placement="right-start" autoSelect={false}>
      <MenuButton
        className={menuStyles.menuTrigger}
        data-testid="user-controls"
      >
        <Avatar size="sm" className={menuStyles.avatar} />
      </MenuButton>
      <Portal>
        <MenuList className={menuStyles.accountList}>
          <MenuGroup title={username as string}>
            {entries.map((entry) =>
              'divider' in entry ? (
                <MenuDivider key={entry.key} />
              ) : (
                <MenuItemWithIcon
                  key={entry.key}
                  onClick={entry.onClick}
                  icon={entry.icon}
                  data-testid={entry.testId}
                >
                  {entry.label}
                </MenuItemWithIcon>
              )
            )}
            {version && (
              <>
                <MenuDivider />
                <MenuGroup
                  title={version}
                  className={menuStyles.versionGroup}
                />
              </>
            )}
          </MenuGroup>
        </MenuList>
      </Portal>
    </Menu>
  );
}

export default AccountMenu;
